/**
 * ========================================
 * PAYMENT SERVICE
 * ========================================
 * API calls for the Payment module (order creation/verification, history,
 * invoices). Endpoints: /api/v1/payments/*
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';
import { snakeToCamelDeep } from './case-transform';
import type {
  CreateOrderResponse,
  PaymentHistoryResponse,
  SubscriptionPlan,
  VerifyPaymentResponse,
} from '@/types/payment';

interface BackendEnvelope<T> {
  status: string;
  status_code?: number;
  message: string;
  data: T;
}

class PaymentService {
  private baseUrl = '/api/v1/payments';

  async listPlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<any[]>>(`${this.baseUrl}/plans`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async createOrder(planId: string, couponCode?: string): Promise<CreateOrderResponse> {
    try {
      const response = await apiClient.post<BackendEnvelope<any>>(`${this.baseUrl}/order`, {
        plan_id: planId,
        coupon_code: couponCode || undefined,
      });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async verifyPayment(razorpayOrderId: string, razorpayPaymentId: string, razorpaySignature: string): Promise<VerifyPaymentResponse> {
    try {
      const response = await apiClient.post<BackendEnvelope<any>>(`${this.baseUrl}/verify`, {
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: razorpayPaymentId,
        razorpay_signature: razorpaySignature,
      });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async getHistory(page = 1, pageSize = 20): Promise<PaymentHistoryResponse> {
    try {
      const response = await apiClient.get<BackendEnvelope<any>>(`${this.baseUrl}/history`, {
        params: { page, page_size: pageSize },
      });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async getInvoice(transactionId: string) {
    try {
      const response = await apiClient.get<BackendEnvelope<any>>(`${this.baseUrl}/${transactionId}/invoice`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }
}

export const paymentService = new PaymentService();
export default paymentService;
